<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="salesReturns"
        :row-key="'id'"
        :loading="loading"
        :pagination="paginationConfig"
        :scroll="{ x: 1300 }"
        class="text-nowrap align-middle sales-return-table"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ t("sales_return_page.no_sales_returns_found") }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'sale_reference'">
            {{ record.reference_no || record.sale?.reference_no || t("sales_return_page.not_available_short") }}
          </template>

          <template v-else-if="column && column.key === 'customer'">
            {{ record.customer_name || record.customer?.name || t("sales_return_page.not_available_short") }}
          </template>

          <template v-else-if="column && column.key === 'warehouse'">
            {{ record.warehouse_name || record.warehouse?.name || t("sales_return_page.not_available_short") }}
          </template>

          <template v-else-if="column && column.key === 'total_items'">
            <span class="badge bg-light text-dark border">
              {{ t("sales_return_page.items_count", { count: record.total_items || record.items?.length || 0 }) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'grand_total'">
            ${{ Number(record.grand_total || 0).toFixed(2) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="`status-${String(record.status || 'completed').toLowerCase()}`">
              {{ formatStatus(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('sales_return_page.view')"
                data-bs-toggle="modal"
                data-bs-target="#salesReturnDetailsModal"
                @click="$emit('view-sales-return', record)"
              >
                <img src="../../../assets/img/icons/eye.svg" :alt="t('sales_return_page.view')" style="width: 14px" />
              </button>
              <router-link
                :to="`/edit-sales-return/${record.id}`"
                :title="t('sales_return_page.edit')"
                class="btn p-0 border-0 action-icon-btn"
              >
                <img src="../../../assets/img/icons/edit.svg" :alt="t('sales_return_page.edit')" style="width: 14px" />
              </router-link>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('sales_return_page.delete')"
                @click="$emit('delete-sales-return', record)"
              >
                <img src="../../../assets/img/icons/close.svg" :alt="t('sales_return_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { SalesReturnRecord } from "@/services/salesReturn";

export default defineComponent({
  name: "SalesReturnList",
  props: {
    salesReturns: {
      type: Array as () => SalesReturnRecord[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["view-sales-return", "delete-sales-return"],
  setup() {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t("sales_return_page.date"), key: "date", align: "center" as const, width: 210 },
      { title: t("sales_return_page.return_reference"), dataIndex: "return_ref", key: "return_ref", align: "center" as const, width: 160 },
      { title: t("sales_return_page.sale_reference"), key: "sale_reference", align: "center" as const, width: 150 },
      { title: t("sales_return_page.customer"), key: "customer", align: "center" as const, width: 200 },
      { title: t("sales_return_page.warehouse"), key: "warehouse", align: "center" as const, width: 180 },
      { title: t("sales_return_page.items"), key: "total_items", align: "center" as const, width: 130 },
      { title: t("sales_return_page.grand_total"), key: "grand_total", align: "center" as const, width: 150 },
      { title: t("sales_return_page.status"), key: "status", align: "center" as const, width: 140 },
      { title: t("sales_return_page.action"), key: "actions", align: "center" as const, width: 120 },
    ]);

    const paginationConfig = computed(() => ({
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "30", "50", "100"],
      defaultPageSize: 10,
      locale: { items_per_page: t("sales_return_page.items_per_page") },
    }));

    const formatDate = (value?: string) => {
      if (!value) return t("sales_return_page.not_available_short");
      return new Date(value).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatStatus = (value?: string) => {
      const normalized = String(value || "completed").toLowerCase();
      const statusKeyMap: Record<string, string> = {
        completed: "sales_return_page.status_completed",
        pending: "sales_return_page.status_pending",
      };

      return statusKeyMap[normalized]
        ? t(statusKeyMap[normalized])
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    return {
      t,
      columns,
      paginationConfig,
      formatDate,
      formatStatus,
    };
  },
});
</script>

<style scoped>
.sales-return-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.table-actions {
  min-width: 72px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}

.status-pending {
  color: #d33c43;
  background: #fdecec;
}
</style>
